import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock } from "vue";
import { ref, watch, onBeforeUnmount } from 'vue';
import ConfigModalBase from '@shared/components/productConfig/ConfigModalBase.vue';
import { useProductPricing } from '@shared/composables/useProductPricing';
import eventBus from '@shared/utils/eventBus';
const PROJECT_ID = 'eckpunkt';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProductConfigModal',
    props: {
        isOpen: { type: Boolean },
        product: {},
        ingredientCategories: {},
        allIngredients: {},
        validationRef: {},
        priceMode: {}
    },
    emits: ["close", "add-to-cart"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { configure, updateConfig, reset, isConfigured, config, calculatePrice, price, priceCent, formattedPrice, isLoading, breakdown, response, error } = useProductPricing();
        const lastPayload = ref(null);
        const activeChannelId = ref(null);
        const lastLoadingState = ref(null);
        const lastResultSignature = ref(null);
        const emitPricingResultEvent = (detail) => {
            eventBus.emit('productPricing:result', detail);
        };
        const emitPricingLoadingEvent = (detail) => {
            eventBus.emit('productPricing:loading', detail);
        };
        const dispatchPendingLoadingState = (isLoadingPending) => {
            if (!activeChannelId.value) {
                return;
            }
            if (lastLoadingState.value === isLoadingPending) {
                return;
            }
            lastLoadingState.value = isLoadingPending;
            const detail = {
                channelId: activeChannelId.value,
                projectId: PROJECT_ID,
                productId: lastPayload.value?.productId ?? config.value?.productId,
                isLoading: isLoadingPending
            };
            emitPricingLoadingEvent(detail);
        };
        onBeforeUnmount(() => {
            activeChannelId.value = null;
            lastLoadingState.value = null;
            lastPayload.value = null;
            lastResultSignature.value = null;
        });
        watch(() => props.product?.id, (productId, oldId) => {
            if (productId && productId !== oldId) {
                lastPayload.value = null;
                activeChannelId.value = null;
                lastLoadingState.value = null;
                lastResultSignature.value = null;
            }
        });
        watch(() => props.isOpen, (isOpen) => {
            if (!isOpen) {
                activeChannelId.value = null;
                lastLoadingState.value = null;
            }
        });
        const handleClose = () => {
            emit('close');
        };
        const handlePricingConfigure = (payload) => {
            activeChannelId.value = payload.channelId;
            lastLoadingState.value = null;
            lastResultSignature.value = null;
            const normalizedPayload = {
                productId: payload.productId,
                quantity: payload.quantity,
                channelId: payload.channelId,
                projectId: PROJECT_ID,
                selectedSizeId: payload.selectedSizeId ?? undefined,
                extraIngredients: payload.extraIngredients?.map(extra => ({
                    id: extra.id,
                    name: extra.name,
                    price: extra.price,
                    priceCent: extra.priceCent ?? extra.price * 100
                })),
                removedIngredients: payload.removedIngredients
            };
            const payloadSignature = JSON.stringify(normalizedPayload);
            const lastSignature = lastPayload.value ? JSON.stringify(lastPayload.value) : null;
            if (payloadSignature === lastSignature) {
                return;
            }
            lastPayload.value = normalizedPayload;
            dispatchPendingLoadingState(true);
            if (!isConfigured.value || !config.value) {
                console.log('[Eckpunkt/ProductConfigModal] Initial configure()', normalizedPayload);
                configure(normalizedPayload);
                void calculatePrice();
                return;
            }
            console.log('[Eckpunkt/ProductConfigModal] Updating config()', normalizedPayload);
            updateConfig(normalizedPayload);
            void calculatePrice();
        };
        const handlePricingReset = () => {
            lastPayload.value = null;
            reset();
            lastResultSignature.value = null;
            if (!activeChannelId.value) {
                return;
            }
            const detail = {
                channelId: activeChannelId.value,
                projectId: PROJECT_ID,
                productId: config.value?.productId,
                result: {
                    price: 0,
                    priceCent: 0,
                    formattedPrice: '0,00 €',
                    isLoading: false
                }
            };
            emitPricingResultEvent(detail);
            dispatchPendingLoadingState(false);
        };
        const syncComposableStateToEvents = () => {
            if (!activeChannelId.value || !config.value) {
                return;
            }
            const resultPayload = {
                price: price.value,
                priceCent: priceCent.value,
                formattedPrice: formattedPrice.value,
                isLoading: isLoading.value,
                breakdown: breakdown.value ?? undefined,
                response: response.value ?? undefined,
                error: error.value ?? undefined
            };
            let signature = '';
            try {
                const signaturePayload = {
                    price: resultPayload.price,
                    priceCent: resultPayload.priceCent,
                    formattedPrice: resultPayload.formattedPrice,
                    isLoading: resultPayload.isLoading,
                    error: resultPayload.error ?? null,
                    breakdown: resultPayload.breakdown ? JSON.stringify(resultPayload.breakdown) : null
                };
                signature = JSON.stringify(signaturePayload);
            }
            catch (_signatureError) {
                signature = [
                    resultPayload.priceCent,
                    resultPayload.formattedPrice,
                    resultPayload.isLoading,
                    resultPayload.error ?? ''
                ].join('|');
            }
            if (lastResultSignature.value !== signature) {
                const detail = {
                    channelId: activeChannelId.value,
                    projectId: PROJECT_ID,
                    productId: config.value.productId,
                    result: resultPayload
                };
                emitPricingResultEvent(detail);
                lastResultSignature.value = signature;
            }
            dispatchPendingLoadingState(isLoading.value);
        };
        watch([price, priceCent, formattedPrice, isLoading, breakdown, error], () => {
            syncComposableStateToEvents();
        }, { immediate: true });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(ConfigModalBase, {
                isOpen: _ctx.isOpen,
                product: _ctx.product,
                ingredientCategories: _ctx.ingredientCategories,
                allIngredients: _ctx.allIngredients,
                validationRef: _ctx.validationRef,
                "price-mode": _ctx.priceMode,
                onClose: handleClose,
                onAddToCart: _cache[0] || (_cache[0] = configuredProduct => emit('add-to-cart', configuredProduct)),
                "onPricing:configure": handlePricingConfigure,
                "onPricing:reset": handlePricingReset
            }, null, 8 /* PROPS */, ["isOpen", "product", "ingredientCategories", "allIngredients", "validationRef", "price-mode"]));
        };
    }
});
